import { useMemo } from 'react';

/**
 * Pure resolver: which attribute controls are hidden given a product's
 * control-visibility config and the live selections. Layered on top of the
 * compatibility tree (which whitelists values); this hides whole controls.
 *
 * A control is hidden when it is default-hidden and no SHOW rule fires, or when
 * a HIDE rule fires. A hidden control is treated as disabled by the caller: its
 * value is not priced, not required, and not saved to the order.
 *
 *   hiddenSlugs — string[] of attribute slugs hidden by default
 *   rules       — [{ trigger_slug, trigger_value_id, action: 'show'|'hide', target_slug }]
 *   selections  — flat map { [attributeSlug]: selectedValueId }
 * → Set<string> of attribute slugs to hide right now.
 */
export function resolveHiddenControlSlugs({ hiddenSlugs = [], rules = [], selections = {} }) {
  const hidden = new Set(hiddenSlugs);
  for (const rule of (rules || [])) {
    if (!rule || !rule.target_slug) continue;
    const sel = selections[rule.trigger_slug];
    const fired = sel != null && Number(sel) === Number(rule.trigger_value_id);
    if (!fired) continue;
    if (rule.action === 'show') hidden.delete(rule.target_slug);
    else if (rule.action === 'hide') hidden.add(rule.target_slug);
  }
  return hidden;
}

/**
 * Dependent-detail guard: select/dropdown controls that exist ONLY because a
 * visibility rule revealed them (default-hidden, currently shown) must be
 * answered before add-to-cart — the customer chose the trigger (e.g. "Klassens
 * bogstav type: Tryk", "Flag: Tryk"), so the detail (the letter, the country)
 * is not optional: production can't act on a paid add-on with no value.
 * Required attrs never appear here (the controls auto-preselect those);
 * pinned attrs carry their value implicitly.
 *
 * → string[] of attribute slugs currently revealed but unanswered.
 */
export function findUnansweredRevealedSlugs({
  attributes = [],
  hiddenSlugs = [],
  hiddenControlSlugs = new Set(),
  selections = {},
}) {
  const defaultHidden = new Set(hiddenSlugs || []);
  const hiddenNow = hiddenControlSlugs instanceof Set ? hiddenControlSlugs : new Set(hiddenControlSlugs || []);
  return (attributes || [])
    .filter((a) => (a.type === 'select' || a.type === 'dropdown')
      && defaultHidden.has(a.slug)
      && !hiddenNow.has(a.slug)
      && (a.values || []).length > 0
      && !(a.pinnedValueIds || []).length
      && selections[a.slug] == null)
    .map((a) => a.slug);
}

/** Memoized hook wrapper around resolveHiddenControlSlugs. */
export default function useControlVisibility({ hiddenSlugs = [], rules = [], selections = {} }) {
  return useMemo(
    () => resolveHiddenControlSlugs({ hiddenSlugs, rules, selections }),
    [hiddenSlugs, rules, selections],
  );
}
